import { countryName } from "@breenova/domain";
import {
  Alert,
  Button,
  Card,
  SegmentedControl,
  StatusPill,
  Textarea,
  cn,
} from "@breenova/ui";
import { ShieldCheck, UserRound } from "lucide-react";
import { useState } from "react";

import { useAuth } from "@/auth/auth-context";
import { DetailHeader } from "@/components/app-shell";
import {
  useAdminUsers,
  useApproveUser,
  useRejectUser,
  useSetUserRoles,
  type AdminUser,
  type ApprovalStatus,
} from "@/lib/admin";
import { SettingsFrame } from "../settings/settings-frame";

type Filter = ApprovalStatus | "All";

const FILTERS: readonly { value: Filter; label: string }[] = [
  { value: "Pending", label: "Waiting" },
  { value: "Approved", label: "Members" },
  { value: "All", label: "Everyone" },
];

/**
 * Who is allowed to use this BreeNova.
 *
 * The screen exists because registration is open and admission is not: anyone can ask for
 * an account, and somebody has to say yes. It opens on the people waiting, because that is
 * the only reason anyone comes here.
 */
export function AdminUsersPage() {
  const { user } = useAuth();
  const [filter, setFilter] = useState<Filter>("Pending");

  const users = useAdminUsers(filter);

  return (
    <div className="flex flex-col gap-md md:gap-lg">
      <DetailHeader backTo="/more" kicker="Administration" title="People" />

      <SettingsFrame>
        <section className="flex w-full flex-col gap-md">
          <SegmentedControl
            label="Which accounts to show"
            size="filter"
            value={filter}
            options={FILTERS}
            onChange={setFilter}
            className="flex w-full"
          />

          {users.data && users.data.pendingCount > 0 && filter !== "Pending" ? (
            <Alert tone="info">
              {users.data.pendingCount === 1
                ? "One person is still waiting to be let in."
                : `${users.data.pendingCount} people are still waiting to be let in.`}
            </Alert>
          ) : null}

          <section aria-live="polite" className="flex flex-col gap-md">
            {users.isPending ? (
              <p className="text-body text-on-surface-variant">Loading the accounts…</p>
            ) : users.isError ? (
              <Card className="flex flex-col items-start gap-sm p-lg">
                <p className="text-body text-error">Could not load the accounts.</p>
                <Button variant="secondary" size="sm" onClick={() => void users.refetch()}>
                  Try again
                </Button>
              </Card>
            ) : users.data.users.length === 0 ? (
              <EmptyState filter={filter} />
            ) : (
              <Card className="flex flex-col">
                {users.data.users.map((account) => (
                  <AccountRow
                    key={account.userId}
                    account={account}
                    isSelf={account.userId === user?.userId}
                  />
                ))}
              </Card>
            )}
          </section>
        </section>
      </SettingsFrame>
    </div>
  );
}

/**
 * One account, and what can be done about it.
 *
 * An administrator's own row shows no buttons at all. The server refuses these decisions
 * about yourself — self-demotion is the one mistake with no way back through the
 * interface — and a button that always fails is worse than no button.
 */
function AccountRow({ account, isSelf }: { account: AdminUser; isSelf: boolean }) {
  const approve = useApproveUser();
  const reject = useRejectUser();
  const setRoles = useSetUserRoles();

  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");

  const busy = approve.isPending || reject.isPending || setRoles.isPending;
  const failure = approve.error ?? reject.error ?? setRoles.error;

  const isAdmin = account.roles.includes("Admin");

  const description = [
    account.countryCode ? countryName(account.countryCode) : null,
    `asked ${relativeTime(account.registeredAt)}`,
    account.lastSignInAt ? `last here ${relativeTime(account.lastSignInAt)}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className={cn(
        "flex flex-col gap-sm p-lg",
        "border-b border-outline-variant last:border-b-0",
      )}
    >
      <div className="flex flex-wrap items-center gap-sm">
        <span
          aria-hidden
          className="flex size-10 shrink-0 items-center justify-center rounded-full bg-surface-container text-primary"
        >
          {isAdmin ? <ShieldCheck className="size-5" /> : <UserRound className="size-5" />}
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-hair">
          <span className="flex flex-wrap items-center gap-sm">
            <span className="truncate text-label text-on-surface">{account.displayName}</span>
            <StatusTag status={account.status} />
            {isAdmin ? <StatusPill tone="biological">Administrator</StatusPill> : null}
            {isSelf ? <StatusPill tone="neutral">You</StatusPill> : null}
          </span>

          <span className="truncate text-caption text-on-surface-variant">{account.email}</span>
          <span className="truncate text-caption text-on-surface-variant">{description}</span>
        </div>
      </div>

      {account.note ? (
        <p className="text-caption text-on-surface-variant">
          Turned away: {account.note}
        </p>
      ) : null}

      {failure ? (
        <Alert tone="error">
          {failure instanceof Error ? failure.message : "That did not work."}
        </Alert>
      ) : null}

      {isSelf ? null : rejecting ? (
        <div className="flex flex-col gap-sm">
          <Textarea
            rows={2}
            value={note}
            maxLength={500}
            placeholder="Why? They will read this."
            onChange={(event) => setNote(event.target.value)}
          />

          <div className="flex flex-wrap gap-sm">
            <Button
              size="sm"
              disabled={busy}
              onClick={() =>
                reject.mutate(
                  { userId: account.userId, note: note.trim() || null },
                  { onSuccess: () => setRejecting(false) },
                )
              }
            >
              Turn away
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setRejecting(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-sm">
          {account.status === "Approved" ? null : (
            <Button
              size="sm"
              disabled={busy}
              onClick={() => approve.mutate({ userId: account.userId })}
            >
              Let in
            </Button>
          )}

          {account.status === "Rejected" ? null : (
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => setRejecting(true)}
            >
              Turn away
            </Button>
          )}

          <Button
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={() =>
              setRoles.mutate({
                userId: account.userId,
                roles: isAdmin ? ["Breeder"] : ["Admin", "Breeder"],
              })
            }
          >
            {isAdmin ? "Remove administrator" : "Make administrator"}
          </Button>
        </div>
      )}
    </div>
  );
}

function StatusTag({ status }: { status: ApprovalStatus }) {
  if (status === "Approved") {
    return <StatusPill tone="positive">Member</StatusPill>;
  }

  if (status === "Rejected") {
    return <StatusPill tone="critical">Turned away</StatusPill>;
  }

  return <StatusPill tone="attention">Waiting</StatusPill>;
}

function EmptyState({ filter }: { filter: Filter }) {
  return (
    <Card className="flex flex-col items-center gap-sm p-xl text-center">
      <p className="text-card-title text-on-surface">
        {filter === "Pending" ? "Nobody is waiting" : "Nothing here"}
      </p>
      <p className="text-body text-on-surface-variant">
        {filter === "Pending"
          ? "New accounts appear here as soon as somebody registers."
          : "No account matches this filter."}
      </p>
    </Card>
  );
}

/**
 * "3 hours ago" rather than a timestamp.
 *
 * The question an administrator is answering is "has this person been sitting there since
 * Tuesday?", and a wall-clock time makes them do the subtraction.
 */
function relativeTime(utc: string): string {
  const minutes = Math.round((Date.now() - new Date(utc).getTime()) / 60_000);

  if (minutes < 1) {
    return "just now";
  }

  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

  if (minutes < 60) {
    return formatter.format(-minutes, "minute");
  }

  const hours = Math.round(minutes / 60);

  if (hours < 24) {
    return formatter.format(-hours, "hour");
  }

  return formatter.format(-Math.round(hours / 24), "day");
}
